import React, { useContext } from 'react';
import { Query } from 'react-apollo';
import { AccountContext } from '../../../context/account';
import { NotebookContext } from '../../../context/NotebookContext';
import client from '../../../lib/client';
import redirect from '../../../lib/redirect';
import {
  AccountMembersQuery,
  AccountMembersQueryVariables
} from '../../../queries/__generated__/AccountMembersQuery';
import {
  InviteMemberToNotebookMutation,
  InviteMemberToNotebookMutationVariables
} from '../../../queries/__generated__/InviteMemberToNotebookMutation';
import { accountMembersQuery } from '../../../queries/account';
import { inviteMemberToNotebookMutation, notebookMembersQuery } from '../../../queries/notebooks';
import { Option, Options, Select, Selected } from '../../forms';
import { Modal } from '../../ui';
import slugify from 'slugify';

interface AddToNotebookModal {
  closeModal: () => void;
  notebookMembers: {
    member: {
      id: string;
    };
  }[];
}

const AddToNotebookModal: React.FC<AddToNotebookModal> = ({ closeModal, notebookMembers }) => {
  const { notebook } = useContext(NotebookContext);
  const [invitee, setInvitee] = React.useState<{
    id: string;
    displayName: string;
    user: { email: string };
  } | null>(null);
  const [isSelectingMember, setIsSelectingMember] = React.useState(false);
  const accountContext = React.useContext(AccountContext);

  async function sendInvite(memberId: string) {
    await client.mutate<InviteMemberToNotebookMutation, InviteMemberToNotebookMutationVariables>({
      mutation: inviteMemberToNotebookMutation,
      variables: { input: { notebookId: notebook.id, memberId } },
      refetchQueries: [
        { query: notebookMembersQuery, variables: { input: { notebookId: notebook.id } } }
      ]
    });
    closeModal();
  }

  return (
    <Query<AccountMembersQuery, AccountMembersQueryVariables>
      query={accountMembersQuery}
      variables={{ input: { accountId: accountContext.selectedAccountId } }}
    >
      {({ data, loading, error }) => {
        if (loading) {
          return <div />;
        }

        const notebookMemberIds = notebookMembers.map(({ member }) => member.id);
        const accountMembers = data!.accountMembers;
        const filteredAccountMembers = accountMembers.filter(
          member => !notebookMemberIds.includes(member.id)
        );

        return (
          <Modal closeModal={closeModal}>
            <div className="pa3">
              <h1 className="tc display f4 fw8 tc">Invite to {notebook.name}</h1>

              <Select>
                <Selected
                  onClick={() => setIsSelectingMember(!isSelectingMember)}
                  data-testid="notebook-members-select"
                >
                  {invitee ? invitee.displayName || invitee.user.email : 'Select a member'}
                </Selected>
                {isSelectingMember && (
                  <Options onClickOutside={() => setIsSelectingMember(false)}>
                    <Option onClick={() => setIsSelectingMember(false)}>
                      <span className="moon-gray">Select a member</span>
                    </Option>

                    {filteredAccountMembers.map(member => (
                      <Option
                        key={member.id}
                        onClick={() => {
                          setInvitee(member);
                          setIsSelectingMember(false);
                        }}
                        data-testid={slugify(member.displayName)}
                      >
                        {member.displayName || member.user.email}
                      </Option>
                    ))}

                    {!filteredAccountMembers.length && (
                      <Option onClick={() => redirect(`/notebook/settings`)}>
                        <span>Invite a new member</span>
                      </Option>
                    )}
                  </Options>
                )}
              </Select>
            </div>

            <div className="flex bt b--light-gray">
              <button
                className="flex-auto pa3 blue hover-navy tc f6 br b--light-gray"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="flex-auto pa3 blue hover-navy tc f6 fw5"
                onClick={() => sendInvite(invitee!.id)}
                disabled={!invitee}
                data-testid="send-invite-button"
              >
                Add Member
              </button>
            </div>
          </Modal>
        );
      }}
    </Query>
  );
};

export default AddToNotebookModal;
